import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import axios from "axios";
import Swal from "sweetalert2";

const apiBaseURL = process.env.REACT_APP_API_BASE_URL;
const serverId = process.env.REACT_APP_SERVER_ID;
const siteId = process.env.REACT_APP_SITE_ID;
const storeId = process.env.REACT_APP_STORE_ID;
const buttonColor = process.env.REACT_APP_BUTTON_COLOR;
const buttonText = process.env.REACT_APP_BUTTON_TEXT;

const partySizeOptions = [1, 2, 3, 4, 5, 6, 7, 8, "other"];

export default function ReservationForm({ revenueCenter }) {
  const customer = JSON.parse(sessionStorage.getItem("customer") || "{}");
  const [timeSlots, setTimeSlots] = useState([]);

  useEffect(() => {
    const menuName = "none";
    const orderType = 0; // Dine In
    const url = `${apiBaseURL}/items/pickup-time-limit/${serverId}/${siteId}/${menuName}/${orderType}`;
    axios
      .get(url)
      .then((response) => {
        if (response.data && response.data.length > 0) {
          const slots = response.data
            .filter((item) => item.OrderType === "Dine In")
            .map((item) => ({
              value: item.PickUpTime,
              label: item.PickUpTime,
            }))
            .sort(
              (a, b) =>
                new Date("1970/01/01 " + a.value) -
                new Date("1970/01/01 " + b.value)
            );
          setTimeSlots(slots);
        }
      })
      .catch((error) => {
        console.error("Error fetching time slots:", error);
      });
  }, [revenueCenter]);

  const [fullName, setFullName] = useState(
    customer.FirstName && customer.LastName
      ? `${customer.FirstName} ${customer.LastName}`
      : ""
  );
  const [phone, setPhone] = useState(customer.Phone || "");
  const [reservationDate, setReservationDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [reservationTime, setReservationTime] = useState("");
  const [partySize, setPartySize] = useState("");
  const [customPartySize, setCustomPartySize] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [flashing, setFlashing] = useState(false);

  const flashErrors = () => {
    setFlashing(true);
    setTimeout(() => setFlashing(false), 5000);
  };

  const flashSx = (hasError) =>
    hasError && flashing
      ? {
          "@keyframes flashBorder": {
            "0%, 100%": { borderColor: "red" },
            "50%": { borderColor: "transparent" },
          },
          "& .MuiOutlinedInput-notchedOutline": {
            animation: "flashBorder 0.6s ease-in-out infinite",
            borderWidth: "2px",
          },
        }
      : {};

  const errors = {
    fullName: submitted && !fullName.trim(),
    phone: submitted && (!phone.trim() || phone.replace(/\D/g, "").length < 10),
    date: submitted && !reservationDate,
    time: submitted && !reservationTime,
    partySize: submitted && !partySize,
    customPartySize: submitted && partySize === "other" && (!customPartySize || parseInt(customPartySize, 10) < 1),
  };

  const validate = () => {
    if (!fullName.trim()) {
      Swal.fire({
        title: "Required Field",
        text: "Please enter your full name.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return false;
    }
    if (!phone.trim() || phone.replace(/\D/g, "").length < 10) {
      Swal.fire({
        title: "Required Field",
        text: "Please enter a valid phone number (at least 10 digits).",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return false;
    }
    if (!reservationDate) {
      Swal.fire({
        title: "Required Field",
        text: "Please select a date for your reservation.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return false;
    }
    if (!reservationTime) {
      Swal.fire({
        title: "Required Field",
        text: "Please select a time for your reservation.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return false;
    }
    if (!partySize) {
      Swal.fire({
        title: "Required Field",
        text: "Please select the number of guests.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return false;
    }
    if (partySize === "other" && (!customPartySize || parseInt(customPartySize, 10) < 1)) {
      Swal.fire({
        title: "Required Field",
        text: "Please enter a valid number of guests.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    setSubmitted(true);
    if (!validate()) {
      flashErrors();
      return;
    }
    setIsSubmitting(true);

    const url = `https://si-web-api.purplemushroom-0993e8c7.westus2.azurecontainerapps.io/v1/${siteId}/customer/reservation`;

    axios
      .post(url, {
        customerID: customer.CustomerID || "",
        firstName: customer.FirstName || "",
        lastName: customer.LastName || "",
        fullName: fullName.trim(),
        email: customer.Email || "",
        phone: phone.trim(),
        roomNumber: customer.Address1 || "",
        date: reservationDate.toISOString().split("T")[0],
        time: reservationTime,
        partySize: partySize === "other" ? parseInt(customPartySize, 10) : parseInt(partySize, 10),
        specialRequests: specialRequests.trim(),
        revenueCenter: sessionStorage.getItem("revenueCenter") || "",
        serverId: serverId,
        siteId: siteId,
        storeId: storeId,
      }, {
        headers: {
          "x-api-key": "66d109b6240cd9d42940ff6e111460a267ce30d83ab13aa6443ef9717b38ef63",
        },
      })
      .then((response) => {
        setIsSubmitting(false);
        Swal.fire({
          title: "Reservation Confirmed!",
          text: `Your reservation for ${partySize === "other" ? customPartySize : partySize} guest(s) on ${reservationDate.toLocaleDateString()} at ${reservationTime} has been submitted.`,
          icon: "success",
          confirmButtonText: "OK",
        });
        setFullName(
          customer.FirstName && customer.LastName
            ? `${customer.FirstName} ${customer.LastName}`
            : ""
        );
        setPhone(customer.Phone || "");
        setReservationDate(null);
        setReservationTime("");
        setPartySize("");
        setCustomPartySize("");
        setSpecialRequests("");
        setSubmitted(false);
      })
      .catch((error) => {
        setIsSubmitting(false);
        console.error("Error submitting reservation:", error);
        Swal.fire({
          title: "Reservation Error",
          text: "We could not process your reservation. Please try again or contact us directly.",
          icon: "error",
          confirmButtonText: "OK",
        });
      });
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "#faf5e6",
          padding: "24px",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{ fontWeight: 600, mb: 0.5, textAlign: "center" }}
        >
          Make a Reservation
        </Typography>
        <Typography
          variant="body1"
          sx={{ mb: 3, textAlign: "center", color: "text.secondary" }}
        >
          Reserve your table at {revenueCenter}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label="Full Name *"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              fullWidth
              variant="outlined"
              error={errors.fullName}
              helperText={errors.fullName ? "Please enter your full name" : ""}
              sx={flashSx(errors.fullName)}
              inputProps={{ style: { fontSize: "16px" } }}
              InputLabelProps={{ style: { fontSize: "16px" } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Phone Number *"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              fullWidth
              variant="outlined"
              type="tel"
              placeholder="(555) 555-1234"
              error={errors.phone}
              helperText={errors.phone ? "Please enter a valid phone number" : ""}
              sx={flashSx(errors.phone)}
              inputProps={{ style: { fontSize: "16px" } }}
              InputLabelProps={{ style: { fontSize: "16px" } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date *"
                value={reservationDate}
                onChange={(date) => {
                  setReservationDate(date);
                  setDateOpen(false);
                }}
                open={dateOpen}
                onOpen={() => setDateOpen(true)}
                onClose={() => setDateOpen(false)}
                disablePast
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    error={errors.date}
                    helperText={errors.date ? "Please select a date" : ""}
                    sx={flashSx(errors.date)}
                    inputProps={{
                      ...params.inputProps,
                      style: { fontSize: "16px" },
                    }}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      style: { fontSize: "16px" },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Time *"
              value={reservationTime}
              onChange={(e) => setReservationTime(e.target.value)}
              fullWidth
              variant="outlined"
              error={errors.time}
              helperText={errors.time ? "Please select a time" : ""}
              sx={flashSx(errors.time)}
              inputProps={{ style: { fontSize: "16px" } }}
              InputLabelProps={{ style: { fontSize: "16px" } }}
            >
              {timeSlots.map((slot) => (
                <MenuItem
                  key={slot.value}
                  value={slot.value}
                  sx={{ fontSize: "16px" }}
                >
                  {slot.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Number of Guests *"
              value={partySize}
              onChange={(e) => {
                setPartySize(e.target.value);
                if (e.target.value !== "other") setCustomPartySize("");
              }}
              fullWidth
              variant="outlined"
              error={errors.partySize}
              helperText={errors.partySize ? "Please select number of guests" : ""}
              sx={flashSx(errors.partySize)}
              inputProps={{ style: { fontSize: "16px" } }}
              InputLabelProps={{ style: { fontSize: "16px" } }}
            >
              {partySizeOptions.map((size) => (
                <MenuItem key={size} value={size} sx={{ fontSize: "16px" }}>
                  {size === "other"
                    ? "More than 8..."
                    : `${size} ${size === 1 ? "Guest" : "Guests"}`}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {partySize === "other" && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Enter Number of Guests *"
                value={customPartySize}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setCustomPartySize(val);
                }}
                fullWidth
                variant="outlined"
                type="tel"
                placeholder="e.g. 12"
                error={errors.customPartySize}
                helperText={errors.customPartySize ? "Please enter number of guests" : ""}
                sx={flashSx(errors.customPartySize)}
                inputProps={{ style: { fontSize: "16px" } }}
                InputLabelProps={{ style: { fontSize: "16px" } }}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              label="Special Requests (Optional)"
              value={specialRequests}
              onChange={(e) => setSpecialRequests(e.target.value)}
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              placeholder="Dietary needs, seating preferences, mobility accommodations..."
              inputProps={{ style: { fontSize: "16px" } }}
              InputLabelProps={{ style: { fontSize: "16px" } }}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                minHeight: 56,
                fontSize: "18px",
                fontWeight: 600,
                backgroundColor: buttonColor,
                color: buttonText,
                "&:hover": {
                  backgroundColor: buttonColor,
                  opacity: 0.9,
                },
              }}
            >
              {isSubmitting ? "Submitting..." : "Reserve a Table"}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
